import { useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Landmark, Wallet } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { MoneyText } from '@/components/ui/MoneyText'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Sheet } from '@/components/ui/Sheet'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { StatTile } from '@/components/ui/StatTile'
import { Textarea } from '@/components/ui/Textarea'
import { useToast } from '@/components/ui/Toast'
import { useProfile } from '@/auth/SessionProvider'
import { subscribeQueue } from '@/attachments/queue'
import { fundBalances, listFunds, listSessions, listTransfers, quarterIncome, reverseLine, reverseTransfer } from '@/data/income'
import { formatDay } from '@/lib/format'
import { reversalsBesideOriginals } from '@/lib/ledgerGroups'
import { formatKwacha as formatMoney } from '@/lib/money'
import { currentQuarterId, quarterRange } from '@/lib/periods'
import { recentQuarters } from '@/lib/reportMath'
import { quarterLabel } from '@/features/budgets/schemas'
import { quarterLabel as shortQuarter } from '@/features/home/insights'
import { CountForm } from './CountForm'
import { FundsPanel } from './FundsPanel'
import { FundsTable } from './FundsTable'
import { SessionRow, TransferRow } from './IncomeRows'
import { TransferForm } from './TransferForm'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const ALL = 'all'

function Failed({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div>
      <p role="alert" className="text-body text-bad">{errText(error)}</p>
      <Button variant="secondary" size="lg" className="mt-3" onClick={retry}>Try again</Button>
    </div>
  )
}

/** One part of the page: a heading, one line saying what it is, an action on the right, and the content in a card. */
function Section({ id, title, description, action, children }: { id: string; title: string; description: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h3 id={id} className="text-section font-semibold text-ink">{title}</h3>
          <p className="mt-0.5 text-body text-muted">{description}</p>
        </div>
        {action}
      </div>
      <Card>
        <CardBody>{children}</CardBody>
      </Card>
    </section>
  )
}

/** The church treasury's own income: what was counted each Sabbath, and what has been passed on. */
export function IncomePage() {
  const profile = useProfile()
  const canWrite = profile.isAdmin
  const qc = useQueryClient()
  const current = currentQuarterId()

  // What the tiles and the two lists below cover: everything to begin with (so they agree with the Funds
  // table, which is always all time), or one quarter chosen from the list.
  const [scope, setScope] = useState<string>(ALL)
  const inScope = scope !== ALL
  const range = inScope ? quarterRange(scope) : null

  const funds = useQuery({ queryKey: ['income', 'funds'], queryFn: listFunds })
  const balances = useQuery({ queryKey: ['income', 'balances'], queryFn: fundBalances })
  const thisQuarter = useQuery({ queryKey: ['income', 'quarter', scope], queryFn: () => quarterIncome(scope), enabled: inScope })
  const sessions = useQuery({ queryKey: ['income', 'sessions'], queryFn: listSessions })
  const transfers = useQuery({ queryKey: ['income', 'transfers'], queryFn: () => listTransfers() })

  // A photo that finishes uploading changes the "No photo yet" badges.
  useEffect(() => subscribeQueue(() => void qc.invalidateQueries({ queryKey: ['income'] })), [qc])

  const [counting, setCounting] = useState(false)
  const [sending, setSending] = useState<string | false>(false) // false = closed, '' = open, id = open on that fund
  const [managing, setManaging] = useState(false)
  const [reversing, setReversing] = useState<{ kind: 'line' | 'transfer'; id: string; what: string } | null>(null)
  const [openSession, setOpenSession] = useState<string | null>(null)

  const activeFunds = (funds.data ?? []).filter((f) => !f.archived)
  const passedOn = (balances.data ?? []).filter((b) => b.destination === 'passed_on' && !b.archived)

  // Counted and kept: one quarter from the quarter totals; all time from the fund balances.
  const countedRows = inScope
    ? (thisQuarter.data ?? []).map((r) => ({ destination: r.destination, ngwee: r.totalNgwee }))
    : (balances.data ?? []).map((b) => ({ destination: b.destination, ngwee: b.receivedNgwee }))
  const countedReady = inScope ? thisQuarter.isSuccess : balances.isSuccess
  const countedFailed = inScope ? thisQuarter.isError : balances.isError
  const countedTotal = countedRows.reduce((s, r) => s + r.ngwee, 0)
  const keptTotal = countedRows.filter((r) => r.destination === 'local').reduce((s, r) => s + r.ngwee, 0)

  const when = !inScope ? 'all time' : scope === current ? 'this quarter' : `in ${shortQuarter(scope)}`
  const span = range ? `Services dated ${formatDay(range.start)} to ${formatDay(range.end)}` : 'Every count since the first'
  const tileValue = (n: number) => (countedFailed ? '—' : countedReady ? <MoneyText ngwee={n} strongZero /> : undefined)

  const sessionsShown = (sessions.data ?? []).filter((s) => !inScope || s.quarterId === scope)
  const transfersShown = reversalsBesideOriginals(
    (transfers.data ?? []).filter((t) => !range || (t.sentOn >= range.start && t.sentOn <= range.end)),
    (t) => t.id,
    (t) => t.reversesId,
  )

  return (
    <div className="flex flex-col gap-10">
      <PageHeader
        title="Church income"
        description={`Tithes and offerings counted after each Sabbath service, and the money passed on to the Conference.${canWrite ? '' : ' You can look at everything here but not change it.'}`}
        actions={
          <Select
            label="Show"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            options={[{ value: ALL, label: 'All time' }, ...recentQuarters(current, 6).map((id) => ({ value: id, label: quarterLabel(id) }))]}
          />
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" aria-label="Income totals">
        <StatTile label={`Counted ${when}`} value={tileValue(countedTotal)} detail={span} />
        <StatTile label={`Stays with the church ${when}`} value={tileValue(keptTotal)} detail={span} />
        <StatTile
          label="Held to pass on"
          value={balances.isError ? '—' : balances.isPending ? undefined : <MoneyText ngwee={passedOn.reduce((s, b) => s + b.heldNgwee, 0)} strongZero />}
          detail="Counted and not yet sent on. Always all time, whichever period is chosen."
        />
      </div>

      <Section
        id="funds-heading"
        title="Funds"
        description="What each fund has received and sent on since it began. Still held is what is waiting to be passed on."
        action={canWrite && <Button variant="secondary" size="lg" onClick={() => setManaging(true)}>Manage funds</Button>}
      >
        {balances.isPending ? <SkeletonRows rows={3} /> : balances.isError ? <Failed error={balances.error} retry={() => void balances.refetch()} /> : (
          <FundsTable
            funds={(balances.data ?? []).filter((b) => !b.archived || b.receivedNgwee !== 0)}
            canWrite={canWrite}
            onSend={(fundId) => setSending(fundId)}
          />
        )}
      </Section>

      <Section
        id="services-heading"
        title="Counted services"
        description={`Each Sabbath count: who counted it, the amounts, and the signed count sheet. ${inScope ? `Showing ${shortQuarter(scope)}.` : 'Showing all of them, newest first.'}`}
        action={canWrite && <Button size="lg" onClick={() => setCounting(true)} disabled={activeFunds.length === 0}>Count a Sabbath</Button>}
      >
        {sessions.isPending ? <SkeletonRows rows={3} /> : sessions.isError ? <Failed error={sessions.error} retry={() => void sessions.refetch()} /> : sessionsShown.length === 0 ? (
          <EmptyState
            icon={Landmark}
            title={(sessions.data ?? []).length === 0 ? 'Nothing counted yet' : `Nothing was counted in ${shortQuarter(scope)}`}
            description={
              (sessions.data ?? []).length === 0
                ? canWrite ? 'After the service, two people count the money. Record the count here, then photograph the signed count sheet.' : 'Nothing has been recorded.'
                : 'Choose All time above to see every count.'
            }
          />
        ) : (
          <ul className="divide-y divide-line" aria-label="Counted services">
            {sessionsShown.map((s) => (
              <SessionRow
                key={s.id}
                s={s}
                canWrite={canWrite}
                expanded={openSession === s.id}
                onToggle={() => setOpenSession(openSession === s.id ? null : s.id)}
                onReverse={(lineId, what) => setReversing({ kind: 'line', id: lineId, what })}
              />
            ))}
          </ul>
        )}
      </Section>

      <Section
        id="sent-heading"
        title="Money sent on"
        description={`Tithe and other passed-on money paid to the Conference, each with its deposit slip. A correction sits under the entry it cancels. ${inScope ? `Showing ${shortQuarter(scope)}.` : ''}`}
      >
        {transfers.isPending ? <SkeletonRows rows={3} /> : transfers.isError ? <Failed error={transfers.error} retry={() => void transfers.refetch()} /> : transfersShown.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title={(transfers.data ?? []).length === 0 ? 'Nothing sent on yet' : `Nothing was sent on in ${shortQuarter(scope)}`}
            description={(transfers.data ?? []).length === 0 ? 'When tithe or another passed-on fund is paid to the Conference, record it here with the deposit slip.' : 'Choose All time above to see every payment.'}
          />
        ) : (
          <ul className="divide-y divide-line" aria-label="Money sent on">
            {transfersShown.map((t) => (
              <TransferRow key={t.id} t={t} canWrite={canWrite} onReverse={() => setReversing({ kind: 'transfer', id: t.id, what: `${formatMoney(t.amountNgwee)} sent from ${t.fundName}` })} />
            ))}
          </ul>
        )}
      </Section>

      <CountForm funds={activeFunds} isOpen={counting} onClose={() => setCounting(false)} />
      <TransferForm funds={passedOn} initialFundId={typeof sending === 'string' && sending ? sending : undefined} isOpen={sending !== false} onClose={() => setSending(false)} />
      <FundsPanel funds={funds.data ?? []} isOpen={managing} onClose={() => setManaging(false)} />
      <ReverseSheet target={reversing} onClose={() => setReversing(null)} />
    </div>
  )
}

function ReverseSheet({ target, onClose }: { target: { kind: 'line' | 'transfer'; id: string; what: string } | null; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const busy = useRef(false)
  const run = useMutation({
    mutationFn: (t: { kind: 'line' | 'transfer'; id: string }) => (t.kind === 'line' ? reverseLine(t.id, reason) : reverseTransfer(t.id, reason)),
  })
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!target || busy.current) return
    setError('')
    if (!reason.trim()) return setError('Say why this is being reversed.')
    busy.current = true
    try {
      await run.mutateAsync(target)
      await qc.invalidateQueries({ queryKey: ['income'] })
      toast.success('Reversed. The original stays on record.')
      setReason('')
      onClose()
    } catch (err) {
      setError(errText(err))
    } finally {
      busy.current = false
    }
  }
  return (
    <Sheet isOpen={target !== null} onClose={onClose} title="Reverse an entry">
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
        <p className="text-body text-ink">
          This adds a visible entry that cancels {target?.what}. The original stays on record and nothing is deleted.
        </p>
        <Textarea label="Why is it being reversed?" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        {error && <p role="alert" className="text-body text-bad">{error}</p>}
        <Button type="submit" size="lg" variant="danger" isLoading={run.isPending}>Reverse it</Button>
      </form>
    </Sheet>
  )
}
